/*
 * Copyright 2026 Sony Corporation
 */
import type { ChatEvent, LogEntry, Run } from '@ips-jit/schema';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { useDeviceLog } from '../hooks/useDeviceLog';
import { rememberDevice } from '../services/device-choice';

import { DesignOptions, designChoiceOf, type DesignChoiceState } from './DesignOptions';
import { SendIcon } from './icons';
import { Markdown } from './Markdown';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const ACTIVE_REFRESH_MS = 2_000;
const IDLE_REFRESH_MS = 10_000;

/** A run is working while it plans or builds; otherwise it is finished or waiting for the user. */
export const isWorking = (run: Run | null): boolean => run?.status === 'planning' || run?.status === 'implementing';

const WORKING_TEXT = { planning: 'Reading the code and planning...', implementing: 'Building the app...' } as const;

const clock = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const Bubble = ({ who, at, children }: { who: 'You' | 'AI'; at: string; children: React.ReactNode }) => (
  <Stack spacing={0.25} sx={{ alignSelf: who === 'You' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, textAlign: who === 'You' ? 'right' : 'left' }}>
      {who} {clock(at)}
    </Typography>
    {children}
  </Stack>
);

const Event = ({ event }: { event: ChatEvent }) => {
  switch (event.type) {
    case 'user':
      return (
        <Bubble who="You" at={event.created_at}>
          <Paper sx={{ p: 1.5, bgcolor: 'primary.dark' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {event.text}
            </Typography>
          </Paper>
        </Bubble>
      );
    case 'plan':
      return (
        <Bubble who="AI" at={event.created_at}>
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.main' }}>
            <Typography variant="overline" color="primary.light">
              Plan
            </Typography>
            <Markdown>{event.text}</Markdown>
          </Paper>
        </Bubble>
      );
    case 'assistant':
      return (
        <Bubble who="AI" at={event.created_at}>
          <Paper sx={{ p: 1.5 }}>
            <Markdown>{event.text}</Markdown>
            {event.version && (
              <Chip size="small" color="success" variant="outlined" label={`Version ${event.version}`} sx={{ mt: 1 }} />
            )}
          </Paper>
        </Bubble>
      );
    case 'error':
      return <Alert severity="error">{event.text}</Alert>;
    case 'status': {
      const [label, ...rest] = event.text.split(/:\s(.*)/s);
      return (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', textAlign: 'center' }}>
          {rest.length > 0 && (
            <Box component="span" sx={{ color: 'info.light', fontWeight: 700 }}>
              {label}:{' '}
            </Box>
          )}
          {rest.length > 0 ? rest[0] : event.text}
        </Typography>
      );
    }
    default:
      return (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
          {event.text}
        </Typography>
      );
  }
};

const logColor = (entry: LogEntry) =>
  entry.level === 'error' ? 'error' : entry.level === 'warn' ? 'warning' : 'default';

/** One line the app on the device reported, shown where it happened in the conversation. */
const LogLine = ({ entry }: { entry: LogEntry }) => (
  <Paper variant="outlined" sx={{ p: 1, alignSelf: 'stretch' }}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        Device log {new Date(entry.ts).toLocaleTimeString()} v{entry.version}
      </Typography>
      <Chip size="small" label={entry.kind} color={logColor(entry)} />
      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {entry.message}
      </Typography>
    </Stack>
  </Paper>
);

type Item =
  { kind: 'event'; event: ChatEvent } | { kind: 'steps'; events: ChatEvent[] } | { kind: 'log'; entry: LogEntry };

/**
 * One timeline of the conversation and the device log, each in its own order. Consecutive steps of the assistant
 * become one item, so the result is not buried under them.
 */
const timeline = (events: ChatEvent[], entries: LogEntry[]): Item[] => {
  const items: Item[] = [];
  let i = 0;
  let j = 0;
  while (i < events.length || j < entries.length) {
    if (i < events.length && (j >= entries.length || Date.parse(events[i].created_at) <= Date.parse(entries[j].ts))) {
      const event = events[i++];
      const last = items.at(-1);
      if (event.type !== 'step') {
        items.push({ kind: 'event', event });
      } else if (last?.kind === 'steps') {
        last.events.push(event);
      } else {
        items.push({ kind: 'steps', events: [event] });
      }
    } else {
      items.push({ kind: 'log', entry: entries[j++] });
    }
  }
  return items;
};

/** The steps of one stretch of work: collapsed, showing the latest, open on request. */
const Steps = ({ events }: { events: ChatEvent[] }) => (
  <Accordion disableGutters variant="outlined" sx={{ alignSelf: 'stretch' }}>
    <AccordionSummary>
      <Typography variant="caption" color="text.secondary" noWrap>
        {events.length === 1 ? '1 step' : `${events.length} steps`}: {events[events.length - 1].text}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={0.5}>
        {events.map((event) => (
          <Typography key={event.seq} variant="body2" color="text.secondary">
            {event.text}
          </Typography>
        ))}
      </Stack>
    </AccordionDetails>
  </Accordion>
);

export interface ProjectChatProps {
  projectId: string;
  /** The device the app is built for; empty when there is none. */
  deviceId: string;
  /** Whether the device log in the conversation holds only errors. */
  errorsOnly: boolean;
  /** Told what the latest request is doing, for the todo list. */
  onProgress?: (progress: { run: Run | null; step?: string }) => void;
}

/**
 * The conversation with the assistant and the device log in one place: describe the app, read its plan, approve it,
 * watch it build, and see what the app reports. The assistant works in the cloud for minutes, so the page asks for
 * what is new every couple of seconds while it does.
 */
export const ProjectChat = ({ projectId, deviceId, errorsOnly, onProgress }: ProjectChatProps) => {
  const { api } = useAuth();
  const [events, setEvents] = useState<ChatEvent[]>([]);
  const [run, setRun] = useState<Run | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const lastSeq = useRef(0);
  const polling = useRef(false);
  const end = useRef<HTMLDivElement>(null);
  const log = useDeviceLog(projectId, errorsOnly);

  const refresh = useCallback(async () => {
    // Slow answers must not pile up: one request at a time keeps the events in order.
    if (polling.current) {
      return;
    }
    polling.current = true;
    try {
      const chat = await api.listChat(projectId, lastSeq.current);
      if (chat.events.length > 0) {
        lastSeq.current = chat.events[chat.events.length - 1].seq;
        setEvents((previous) => [...previous, ...chat.events]);
      }
      setRun(chat.run);
    } catch (caught) {
      setError(describe(caught, 'Could not load the chat.'));
    } finally {
      polling.current = false;
    }
  }, [api, projectId]);

  const working = isWorking(run);
  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), working ? ACTIVE_REFRESH_MS : IDLE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh, working]);

  const items = useMemo(() => timeline(events, log.entries), [events, log.entries]);

  const step = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].type === 'step' && events[i].run_id === run?.run_id) {
        return events[i].text;
      }
    }
    return undefined;
  }, [events, run?.run_id]);
  useEffect(() => {
    onProgress?.({ run, step });
  }, [run, step, onProgress]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [items.length]);

  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setRun(await api.sendMessage(projectId, { text, ...(deviceId ? { device_id: deviceId } : {}) }));
      if (deviceId) {
        rememberDevice(projectId, deviceId);
      }
      setText('');
      await refresh();
    } catch (caught) {
      setError(describe(caught, 'Could not send the message.'));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (approved: boolean) => {
    if (!run) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      setRun(await api.decideRun(projectId, run.run_id, approved, approved ? designChoiceOf(designChoice) : undefined));
      await refresh();
    } catch (caught) {
      setError(describe(caught, 'Could not send your answer.'));
    } finally {
      setBusy(false);
    }
  };

  const waiting = run?.status === 'awaiting_approval';
  const [designChoice, setDesignChoice] = useState<DesignChoiceState>({ optionId: '', note: '' });
  useEffect(() => setDesignChoice({ optionId: '', note: '' }), [run?.run_id]);
  const hasDesigns = waiting && run.design_options.some((o) => o.state === 'ready');
  const shownError = error ?? log.error;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack
        spacing={1}
        role="log"
        aria-label="Conversation"
        sx={{ flex: 1, minHeight: { xs: 320, md: 0 }, overflowY: 'auto', pr: 1 }}
      >
        {items.length === 0 && (
          <Typography color="text.secondary">
            Say what the app should do, for example "When I capture, read the QR code in the picture and show its URL."
            The assistant reads the code, proposes a plan, and builds it once you approve.
          </Typography>
        )}
        {items.map((item) =>
          item.kind === 'steps' ? (
            <Steps key={`steps-${item.events[0].seq}`} events={item.events} />
          ) : item.kind === 'log' ? (
            <LogLine
              key={`log-${item.entry.version}-${item.entry.ts}-${item.entry.kind}-${item.entry.message}`}
              entry={item.entry}
            />
          ) : (
            <Event key={`event-${item.event.seq}`} event={item.event} />
          )
        )}
        <Box ref={end} />
      </Stack>
      {working && run && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {WORKING_TEXT[run.status as keyof typeof WORKING_TEXT]}
          </Typography>
          <LinearProgress />
        </Box>
      )}
      {shownError && <Alert severity="error">{shownError}</Alert>}
      {hasDesigns && (
        <DesignOptions
          projectId={projectId}
          run={run}
          choice={designChoice}
          onChange={setDesignChoice}
          disabled={busy}
        />
      )}
      {waiting && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" disabled={busy} onClick={() => void decide(false)}>
                Reject
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={busy || !run.device_id || (hasDesigns && designChoice.optionId === '')}
                onClick={() => void decide(true)}
              >
                Approve and build
              </Button>
            </Stack>
          }
        >
          {run.device_id
            ? hasDesigns && designChoice.optionId === ''
              ? 'Choose a design direction above, then approve the plan to build the app, or reject it and say what to change.'
              : 'Approve the plan to build the app and send it to your device, or reject it and say what to change.'
            : 'No device was chosen for this request. Choose one and send your request again to build it.'}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'stretch' }}>
        <TextField
          placeholder="Describe your app"
          slotProps={{ htmlInput: { 'aria-label': 'Message' } }}
          multiline
          maxRows={6}
          fullWidth
          size="small"
          sx={{ '& .MuiInputBase-input': { typography: 'body2' } }}
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={working}
        />
        <IconButton
          color="primary"
          aria-label="Send"
          disabled={busy || working || text.trim() === ''}
          onClick={() => void send()}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, alignSelf: 'stretch', px: 1 }}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
};
